import * as React from "react";
import type {
  PositioningImperativeRef,
  PositioningVirtualElement,
} from "@fluentui/react-components";
import type { Point } from "@fluentui/utilities";
import type { AppContextMenuProps } from "../next/AppContextualMenu/AppContextualMenu.types";
import { performOnNextFrame } from "@1js/acui-common";
import { AppContext } from "@1js/acui-context";
import { checkExperiment, ribbonExperiments } from "@1js/acui-experiments";

function isPoint(target: any): target is Point {
  return "x" in target && "y" in target;
}

function isRefObject(target: any): target is React.RefObject<Element> {
  return target && "current" in target;
}

export const useMenuPositioning = (
  props: AppContextMenuProps
): React.RefObject<PositioningImperativeRef> => {
  const positioningRef = React.useRef<PositioningImperativeRef | null>(null);
  const [location, setLocation] = React.useState<{
    x: number | undefined;
    y: number | undefined;
  } | null>(null);

  const context = React.useContext(AppContext);
  const isV9ContextMenuPositionFixEnabled = !!checkExperiment(
    context,
    ribbonExperiments.isV9ContextMenuPositionFixEnabled
  );
  React.useEffect(() => {
    if (isV9ContextMenuPositionFixEnabled) {
      let x: number | undefined;
      let y: number | undefined;

      if (props?.target) {
        if (isPoint(props.target)) {
          x = props.target?.x;
          y = props.target?.y;
        } else if (isRefObject(props.target)) {
          const rect = props.target?.current?.getBoundingClientRect();
          x = rect?.x;
          y = rect?.y;
        } else {
          // TODO: handle this later
        }
      }

      setLocation((prevLocation) => {
        if (prevLocation?.x !== x || prevLocation?.y !== y) {
          return { x, y };
        }
        return prevLocation;
      });
    }
  }, [props, isV9ContextMenuPositionFixEnabled]);

  React.useLayoutEffect(() => {
    const target = props?.target;
    const onPositioningEnd = props?.onPositioningEnd;

    // check if target is defined or not
    if (target !== undefined) {
      // if target is a point, create a virtual element at (x,y) and set it as target
      if (isPoint(target)) {
        const getRect = (x = 0, y = 0) => {
          return () => ({
            width: 0,
            height: 0,
            top: y,
            right: x,
            bottom: y,
            left: x,
            x,
            y,
          });
        };
        const virtualElement: PositioningVirtualElement = {
          getBoundingClientRect: getRect(target.x, target.y),
        };
        positioningRef.current?.setTarget(virtualElement);

        // The onPositioned callback is called after positioning is complete.
        // We use performOnNextFrame to ensure that the callback is called after the positioning is complete.
        onPositioningEnd && performOnNextFrame(onPositioningEnd);
      }

      // if target is a ref object, check if its current is not null then set it as target
      else if (isRefObject(target)) {
        if (target?.current) {
          positioningRef.current?.setTarget(target.current);
        }
      }

      // if target is Element | string | MouseEvent |  Rectangle | null
      else {
        // TODO: handle this later
      }
    }
  }, [props.target, props.onPositioningEnd, location]);

  return positioningRef;
};
